import { create } from 'zustand';
import { encryptJSON, decryptJSON, EncryptedPayload } from '../utils/crypto';
import { useLicenseStore } from './license';

// Company domain model used across UI modules
export type Company = {
  id: string;
  name: string;
  country: string;
  industry: string;
  employees: number;
};

type CompanyVault = {
  companies: Company[];
  activeId: string | null;
  updatedAt: string;
};

type CompanyState = {
  companies: Company[];
  activeId: string | null;
  setActive: (id: string | null) => void;
  addCompany: (c: Omit<Company, 'id'>) => Promise<string>;
  updateCompany: (id: string, changes: Partial<Omit<Company, 'id'>>) => Promise<void>;
  removeCompany: (id: string) => Promise<void>;
  load: () => Promise<void>;
};

const STORAGE_KEY = 'evat_companies_encrypted';

export const useCompanyStore = create<CompanyState>((set, get) => ({
  companies: [],
  activeId: null,
  setActive: (id) => set({ activeId: id }),
  addCompany: async (c) => {
    const id = crypto.randomUUID();
    const next = { ...c, id } satisfies Company;
    const companies = [...get().companies, next];
    set({ companies, activeId: id });
    await persist(companies, id);
    return id;
  },
  updateCompany: async (id, changes) => {
    const companies = get().companies.map((x) => (x.id === id ? { ...x, ...changes } : x));
    set({ companies });
    await persist(companies, get().activeId);
  },
  removeCompany: async (id) => {
    const companies = get().companies.filter((x) => x.id !== id);
    const activeId = get().activeId === id ? (companies[0]?.id ?? null) : get().activeId;
    set({ companies, activeId });
    await persist(companies, activeId);
  },
  load: async () => {
    const payloadRaw = localStorage.getItem(STORAGE_KEY);
    const license = useLicenseStore.getState().licenseKey;
    if (!payloadRaw || !license) return;
    try {
      const payload: EncryptedPayload = JSON.parse(payloadRaw);
      const vault = await decryptJSON<CompanyVault>(license, payload);
      set({ companies: vault.companies, activeId: vault.activeId });
    } catch {}
  }
}));

async function persist(companies: Company[], activeId: string | null) {
  const license = useLicenseStore.getState().licenseKey;
  if (!license) return; // Require license to encrypt vault
  const vault: CompanyVault = { companies, activeId, updatedAt: new Date().toISOString() };
  const payload = await encryptJSON(license, vault);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}



















